import { useState, useEffect } from 'react';
import { ChefHat, ChevronLeft, AlertTriangle, Check, Loader2, Clock, X } from 'lucide-react';
import { FIALOVA, csrf, datum, styly, Nadpis, Zpet, Hledani, Posun, zapisNedavny } from './ui';
import ZnalostiOdznak, { usePoctyZnalosti } from '../../../components/znalosti/ZnalostiOdznak';

// Recepty v řízené dokumentaci obsluhy: hledání (bez diakritiky, i v surovinách)
// a detail v pořadí pro pult — fotka, změna k potvrzení, alergeny, suroviny,
// postup. Ceny ani kalkulace sem nepatří.

const POZNAMKA = {
    bezpecnost: { label: 'Bezpečnost', color: '#F87171' },
    kvalita:    { label: 'Kvalita', color: '#FBBF24' },
    zrucnost:   { label: 'Zručnost', color: '#C79BF0' },
};
const STRANKA = 60;

// Miniatura receptu — fotka hotového produktu, jinak tlumená plocha s ikonou
export function Nahled({ recept, velikost = 54, zaobleni = 10 }) {
    const [chyba, setChyba] = useState(false);
    const box = { width: velikost, height: velikost, borderRadius: zaobleni, flexShrink: 0, overflow: 'hidden' };
    if (recept?.foto_url && !chyba) {
        return <img src={recept.foto_url} alt="" loading="lazy" onError={() => setChyba(true)} style={{ ...box, objectFit: 'cover' }} />;
    }
    return (
        <div className="flex items-center justify-center" style={{ ...box, background: 'linear-gradient(135deg, rgba(255,138,0,0.28), rgba(151,90,205,0.18))', color: 'rgba(255,255,255,0.8)' }}>
            <ChefHat size={typeof velikost === 'number' ? Math.round(velikost * 0.45) : 56} />
        </div>
    );
}

export function ReceptySeznam({ isDark, onZpet, onOtevrit }) {
    const s = styly(isDark);
    const [q, setQ] = useState('');
    const [kategorie, setKategorie] = useState(null);
    const [kategorieSeznam, setKategorieSeznam] = useState([]);
    const [recepty, setRecepty] = useState(null);
    const [limit, setLimit] = useState(STRANKA);

    useEffect(() => {
        fetch('/api/me/dokumentace/recepty/kategorie').then(r => r.ok ? r.json() : []).then(setKategorieSeznam).catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(() => {
            const p = new URLSearchParams();
            if (q.trim()) p.set('q', q.trim());
            if (kategorie) p.set('kategorie_id', kategorie);
            fetch(`/api/me/dokumentace/recepty?${p}`).then(r => r.ok ? r.json() : []).then(data => { setRecepty(data); setLimit(STRANKA); }).catch(() => setRecepty([]));
        }, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [q, kategorie]);

    const chip = (aktivni) => ({
        flexShrink: 0, fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
        background: aktivni ? 'var(--brand-bg)' : s.plocha, color: aktivni ? 'var(--brand-text)' : 'var(--text-secondary)',
    });

    return (
        <Posun>
            <Zpet label="Znalosti" onClick={onZpet} />
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', margin: '4px 0 12px' }}>Recepty</h1>
            <Hledani value={q} onChange={setQ} isDark={isDark} placeholder="Název, kód nebo surovina…" />

            {kategorieSeznam.length > 0 && (
                <div className="flex gap-1.5" style={{ overflowX: 'auto', marginTop: 12, paddingBottom: 2, scrollbarWidth: 'none' }}>
                    <button onClick={() => setKategorie(null)} style={chip(!kategorie)}>Vše</button>
                    {kategorieSeznam.map(k => (
                        <button key={k.id} onClick={() => setKategorie(kategorie === k.id ? null : k.id)} style={chip(kategorie === k.id)}>{k.nazev}</button>
                    ))}
                </div>
            )}

            {!recepty ? (
                <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
            ) : <>
                <p className="text-[11px]" style={{ color: 'var(--text-muted)', margin: '10px 0 8px' }}>
                    {recepty.length === 0 ? 'Nic jsem nenašel.' : `${recepty.length} ${recepty.length === 1 ? 'recept' : recepty.length < 5 ? 'recepty' : 'receptů'}`}
                </p>
                {recepty.slice(0, limit).map(r => (
                    <button key={r.id} onClick={() => onOtevrit(r.id)} className="w-full flex items-center gap-3 text-left"
                        style={{ ...s.karta, padding: 10, marginBottom: 8, cursor: 'pointer' }}>
                        <Nahled recept={r} />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                                <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{r.nazev}</span>
                                {r.typ === 'SEM' && <span className="text-[10px] font-bold rounded-full shrink-0" style={{ padding: '2px 7px', background: 'rgba(151,90,205,0.18)', color: FIALOVA }}>Polotovar</span>}
                            </div>
                            <div className="text-[11px] truncate" style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                                {r.nalezeno_v ? `Nalezeno v surovinách: ${r.nalezeno_v}` : [r.kategorie?.nazev, r.alergeny?.length ? `alergeny ${r.alergeny.join(', ')}` : null].filter(Boolean).join(' · ')}
                            </div>
                            {r.zmena && <span className="inline-block text-[10.5px] font-bold rounded-full" style={{ marginTop: 4, padding: '2px 8px', background: 'rgba(245,158,11,0.14)', color: '#FBBF24' }}>Změna {datum(r.zmena, false)}</span>}
                        </div>
                    </button>
                ))}
                {recepty.length > limit && (
                    <button onClick={() => setLimit(l => l + STRANKA)} className="w-full text-sm font-semibold" style={{ padding: 12, color: 'var(--brand-text)', background: 'none', cursor: 'pointer' }}>
                        Zobrazit další ({recepty.length - limit})
                    </button>
                )}
            </>}
        </Posun>
    );
}

export function ReceptDetail({ id, isDark, onZpet, onOtevrit, onPotvrzeno }) {
    const s = styly(isDark);
    const [r, setR] = useState(null);
    // Jednobodovky ke krokům (přímo nebo přes stroj kroku) — ikonka s proklikem
    const poctyZnalosti = usePoctyZnalosti('sop_krok', r?.kroky?.map(k => k.id));
    const [chyba, setChyba] = useState(null);
    const [potvrzuji, setPotvrzuji] = useState(false);
    const [foto, setFoto] = useState(null);

    useEffect(() => {
        fetch(`/api/me/dokumentace/recepty/${id}`)
            .then(res => res.ok ? res.json() : Promise.reject(res.status))
            .then(data => { setR(data); zapisNedavny(data); })
            .catch(() => setChyba('Recept se nepodařilo načíst.'));
    }, [id]);

    const potvrdit = async () => {
        setPotvrzuji(true);
        const res = await fetch(`/api/me/dokumentace/recepty/${id}/potvrdit`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
        if (res.ok) {
            setR(prev => ({ ...prev, zmena: null }));
            onPotvrzeno?.();
        }
        setPotvrzuji(false);
    };

    if (chyba) return <Posun><Zpet label="Recepty" onClick={onZpet} /><p className="text-sm" style={{ color: 'var(--text-muted)', marginTop: 24 }}>{chyba}</p></Posun>;
    if (!r) return <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

    const davka = r.davka?.mnozstvi ? `${Number(r.davka.mnozstvi).toLocaleString('cs-CZ')} ${r.davka.jednotka || ''}`.trim() : null;

    return (
        <Posun>
            {/* Fotka hotového produktu */}
            <div style={{ position: 'relative', height: 210, borderRadius: 14, overflow: 'hidden' }}>
                {r.foto_url
                    ? <img src={r.foto_url} alt={r.nazev} onClick={() => setFoto(r.foto_url)} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }} />
                    : <Nahled recept={r} velikost="100%" zaobleni={14} />}
                <button onClick={onZpet} aria-label="Zpět" className="flex items-center justify-center"
                    style={{ position: 'absolute', left: 14, top: 12, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', color: '#fff', cursor: 'pointer' }}>
                    <ChevronLeft size={20} />
                </button>
            </div>

            <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 12 }}>
                <h1 className="text-[22px] font-bold" style={{ color: 'var(--text-primary)' }}>{r.nazev}</h1>
                {r.varianta && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.varianta}</span>}
                {r.typ === 'SEM' && <span className="text-[10px] font-bold rounded-full" style={{ padding: '2px 7px', background: 'rgba(151,90,205,0.18)', color: FIALOVA }}>Polotovar</span>}
            </div>
            {(r.kategorie || r.cas_pripravy_min) && (
                <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                    {r.kategorie?.nazev}
                    {r.cas_pripravy_min && <><Clock size={12} /> {r.cas_pripravy_min} min</>}
                </p>
            )}

            {r.zmena && (
                <div style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 12, padding: '10px 12px', marginTop: 12 }}>
                    <div className="flex gap-2 items-start">
                        <AlertTriangle size={17} style={{ color: '#FBBF24', flexShrink: 0, marginTop: 1 }} />
                        <p className="text-[12.5px]" style={{ color: 'var(--text-primary)', lineHeight: 1.4 }}>
                            <b>Změna {datum(r.zmena.datum, false)}.</b> {r.zmena.popis || 'Recept se změnil.'} Potvrď, že ses se změnou seznámil.
                        </p>
                    </div>
                    <div className="flex justify-end" style={{ marginTop: 8 }}>
                        <button onClick={potvrdit} disabled={potvrzuji} className="flex items-center gap-1.5 text-[12.5px] font-bold rounded-full"
                            style={{ height: 32, padding: '0 14px', background: 'var(--brand)', color: '#1a1000', cursor: 'pointer', opacity: potvrzuji ? 0.6 : 1 }}>
                            {potvrzuji ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Potvrzuji
                        </button>
                    </div>
                </div>
            )}

            {r.alergeny?.length > 0 && <>
                <Nadpis>Alergeny</Nadpis>
                <div className="flex flex-wrap gap-1.5">
                    {r.alergeny.map(a => (
                        <span key={a.cislo_eu ?? a.nazev} className="text-xs font-bold rounded-full" style={{ padding: '5px 10px', background: 'rgba(239,68,68,0.14)', color: '#F87171' }}>
                            {a.cislo_eu ? `${a.cislo_eu} ` : ''}{a.nazev}
                        </span>
                    ))}
                </div>
            </>}

            {r.ingredience?.length > 0 && <>
                <Nadpis>Suroviny{davka ? ` · dávka ${davka}` : ''}</Nadpis>
                <div style={{ ...s.tlumenaKarta, padding: '2px 12px' }}>
                    {r.ingredience.map((i, idx) => {
                        const polotovar = i.typ === 'SEM' && i.recept_id;
                        return (
                            <div key={idx} onClick={polotovar ? () => onOtevrit(i.recept_id) : undefined}
                                className="flex justify-between gap-3 text-[13px]"
                                style={{ padding: '7px 0', borderTop: idx ? `1px solid ${s.linka}` : 'none', cursor: polotovar ? 'pointer' : 'default' }}>
                                <span style={{ color: polotovar ? FIALOVA : 'var(--text-primary)' }}>{i.nazev}{polotovar ? ' ›' : ''}</span>
                                <b style={{ color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{Number(i.spotreba).toLocaleString('cs-CZ', { maximumFractionDigits: 3 })} {i.jednotka}</b>
                            </div>
                        );
                    })}
                </div>
            </>}

            {r.kroky?.length > 0 && <>
                <Nadpis>Postup</Nadpis>
                <div className="space-y-3">
                    {r.kroky.map(k => (
                        <div key={k.id} className="flex gap-2.5 items-start">
                            <div className="flex items-center justify-center text-xs font-bold shrink-0"
                                style={{ width: 24, height: 24, borderRadius: '50%', background: k.kriticky_bod ? 'rgba(239,68,68,0.16)' : s.plocha, color: k.kriticky_bod ? '#F87171' : 'var(--text-secondary)' }}>
                                {k.krok}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                                    {k.nazev}
                                    {k.kriticky_bod && <span className="text-[10px] font-bold rounded-full" style={{ marginLeft: 6, padding: '2px 7px', background: 'rgba(239,68,68,0.14)', color: '#F87171' }}>Kritický bod</span>}
                                </div>
                                {k.popis && <p className="text-xs" style={{ color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.45 }}>{k.popis}</p>}
                                {poctyZnalosti[k.id] > 0 && <div style={{ marginTop: 6 }}><ZnalostiOdznak typ="sop_krok" id={k.id} pocet={poctyZnalosti[k.id]} isDark={isDark} kompaktni /></div>}
                                {k.cas_min && <p className="text-[11px]" style={{ color: 'var(--text-muted)', marginTop: 2 }}>{k.cas_min} min{k.stroje?.length ? ` · ${k.stroje.map(x => x.nazev).join(', ')}` : ''}</p>}
                                {k.poznamky?.map(p => {
                                    const cfg = POZNAMKA[p.typ] || POZNAMKA.kvalita;
                                    return (
                                        <p key={p.id} className="text-[11.5px]" style={{ marginTop: 5, paddingLeft: 8, borderLeft: `2px solid ${cfg.color}`, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                                            <b style={{ color: cfg.color }}>{cfg.label}:</b> {p.text}
                                        </p>
                                    );
                                })}
                                {(k.media?.length > 0 || k.reference?.length > 0) && (
                                    <div className="flex gap-1.5 flex-wrap" style={{ marginTop: 6 }}>
                                        {k.media?.map((m, i) => (
                                            <img key={`m${i}`} src={m.nahled} alt={m.popis || ''} loading="lazy" onClick={() => setFoto(m.url)}
                                                style={{ width: 64, height: 44, borderRadius: 8, objectFit: 'cover', cursor: 'zoom-in' }} />
                                        ))}
                                        {k.reference?.map((x, i) => (
                                            <img key={`r${i}`} src={x.nahled} alt={x.typ === 'spravne' ? 'Správně' : 'Špatně'} loading="lazy" onClick={() => setFoto(x.url)}
                                                title={x.typ === 'spravne' ? 'Správně' : 'Špatně'}
                                                style={{ width: 64, height: 44, borderRadius: 8, objectFit: 'cover', cursor: 'zoom-in', border: `2px solid ${x.typ === 'spravne' ? '#10b981' : '#EF4444'}` }} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </>}

            {r.poznamka && <>
                <Nadpis>Poznámka</Nadpis>
                <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.45 }}>{r.poznamka}</p>
            </>}

            {r.posledni_revize && (
                <p className="text-[11px]" style={{ color: 'var(--text-muted)', margin: '18px 0 4px' }}>
                    Poslední revize {r.posledni_revize.datum}{r.posledni_revize.uzivatel ? ` · ${r.posledni_revize.uzivatel}` : ''}
                </p>
            )}

            {foto && (
                <div onClick={() => setFoto(null)} className="flex items-center justify-center"
                    style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.88)', padding: 16 }}>
                    <img src={foto} alt="" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 10 }} />
                    <button aria-label="Zavřít" className="flex items-center justify-center"
                        style={{ position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', right: 16, width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                        <X size={18} />
                    </button>
                </div>
            )}
        </Posun>
    );
}
